import React, { useContext } from 'react';
import { EDListItemButton } from '../../../../../../../../../BaseComponents/List/Components/EDListItem';
import EDListItemText from '../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import EDCollapse from '../../../../../../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import { IconType } from '../../../../../../../../../BaseComponents/Icon/Utils/IconDefinitions';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import { sideMenuListItemGetScrollTop } from './Utils/EkonomiskAnalysExpandableListItemUtils';
import ChangedMarker from '../../../../../../../../../Components/ChangedMarker/ChangedMarker';
import EDTooltip from '../../../../../../../../../BaseComponents/Tooltip/EDTooltip';

interface IProps {
  text: string;
  initialOpen?: boolean;
  iconType?: IconType;
  changed?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  rightSideIcon?: boolean;
  classes?: any;
  children?: React.ReactNode;
}

interface IInnerProps extends IProps {
  scrollContainerEl: HTMLDivElement | null;
}

const styles = (theme: Theme) => ({
  listItemText: {
    paddingLeft: theme.spacing(6),
    '&:first-of-type': {
      paddingLeft: theme.spacing(6),
    },
  },
  listItemLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  listItemTextWithIcon: {
    '&&': { paddingLeft: theme.spacing(1) },
  },
  icon: {
    marginLeft: theme.spacing(1),
    color: theme.palette.secondary.light,
  },
  collapse: {
    paddingLeft: theme.spacing(5),
    paddingRight: '36px',
  },
  rightSideIcon: {
    paddingRight: 12,
  },
  collapseOpen: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  listItem: {
    paddingRight: '36px',
  },
  open: {
    borderBottom: `1px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
    backgroundColor: theme.customProperties.Fade.BlueGray.lowEmphasis,
  },
});

/**
 * Expanderbart inre listelement
 */
export class EkonomiskAnalysExpandableNestedListItem extends React.Component<IInnerProps> {
  listItemRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  animationFrameID: number | undefined;

  constructor(props: IInnerProps) {
    super(props);

    this.listItemRef = React.createRef();
    this.contentRef = React.createRef();
  }

  onClick = () => {
    this.props.setOpen(!this.props.open);
  };

  contentOnEntering = () => {
    const { scrollContainerEl } = this.props;

    if (
      this.listItemRef.current &&
      scrollContainerEl &&
      this.contentRef.current
    ) {
      const scrollTop = sideMenuListItemGetScrollTop(
        this.listItemRef.current,
        this.contentRef.current,
        scrollContainerEl
      );
      if (scrollTop !== -1) {
        const animationFrameCallback = () => {
          scrollContainerEl.scrollTop = scrollTop;
          this.animationFrameID = requestAnimationFrame(animationFrameCallback);
        };

        this.animationFrameID = requestAnimationFrame(animationFrameCallback);
      }
    }
  };

  contentOnEntered = () => {
    if (this.animationFrameID) {
      cancelAnimationFrame(this.animationFrameID);
    }
  };

  contentOnExited = () => {
    if (this.animationFrameID) {
      cancelAnimationFrame(this.animationFrameID);
    }
  };

  render() {
    const { classes, text } = this.props;

    const open = this.props.disabled ? false : this.props.open;

    return (
      <>
        <EDTooltip
          title={this.props.disabled ? this.props.disabledReason : undefined}
          enableClickOnTouch={this.props.disabled}
        >
          <span>
            <EDListItemButton
              ref={this.listItemRef}
              onClick={this.onClick}
              className={classNames(classes.listItem, {
                [classes.open]: open,
              })}
              disabled={this.props.disabled}
            >
              {this.props.iconType && (
                <EDIcon iconType={this.props.iconType} color="inherit" />
              )}
              <EDListItemText
                primary={
                  <div className={this.props.classes.listItemLabel}>
                    {text}
                    {this.props.changed && <ChangedMarker leftMargin />}
                  </div>
                }
                className={classNames(classes.listItemText, {
                  [classes.listItemTextWithIcon]: this.props.iconType,
                })}
                primaryTypographyProps={{ variant: 'body2' }}
              />
              {open ? (
                <EDIcon iconType="expand_less" />
              ) : (
                <EDIcon iconType="expand_more" />
              )}
            </EDListItemButton>
          </span>
        </EDTooltip>
        <EDCollapse
          open={open}
          timeout="auto"
          className={classNames(classes.collapse, {
            [classes.rightSideIcon]: this.props.rightSideIcon,
          })}
          onEntering={this.contentOnEntering}
          onEntered={this.contentOnEntered}
          onExited={this.contentOnExited}
        >
          <div ref={this.contentRef} className={classes.collapseOpen}>
            {this.props.children}
          </div>
        </EDCollapse>
      </>
    );
  }
}

const EnhancedEkonomiskAnalysExpandableNestedListItem: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { scrollContainerRef } = useContext(EkonomiskAnalysSideMenuContext);
  return (
    <EkonomiskAnalysExpandableNestedListItem
      scrollContainerEl={scrollContainerRef.current}
      {...props}
    />
  );
};

export default withStyles(
  EnhancedEkonomiskAnalysExpandableNestedListItem,
  styles
);
